import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { Dialog } from 'primereact/dialog'
import { Map as ImmutableMap } from 'immutable'
import useSession from '@aytm/app/swidget/base/session/useSession.ts'
import useTheme from '@aytm/app/swidget/base/modules/theme/useTheme.ts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'
import ExitSimulation from '@aytm/app/swidget/base/questions/_common/ExitSimulation.tsx'
import { IEcommerceQuestionViewProps } from '../types.ts'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useLoadScriptInFrame from '@aytm/app/swidget/base/questions/Ecommerce/hooks/useLoadScriptInFrame.ts'
import style from './Costco.module.scss'
import Header from './components/Header.tsx'
import Main from './components/Main.tsx'
import Product from './components/Product.tsx'
import Cart from './components/Cart.tsx'
import Footer from './components/Footer.tsx'

const CostcoView = ({ schema, response, checkout, setClosed, onUpdateResponse }: IEcommerceQuestionViewProps) => {
  const session = useSession()
  const na = useMemo(() => schema.answers.find(answer => answer.na), [schema])
  const totals = [
    response.answers.reduce((prev, cur) => prev + (cur.id === na?.id ? 0 : cur.v), 0),
    response.answers.reduce((prev, cur) => prev + cur.v * cur.price, 0)
  ]
  const selectedIndex = response.answers.findIndex(r => r.selected)
  const timeline = useTimeline()
  const findability = 'find' == schema.mode_type || (1 == schema.max_cart_size && !schema.shopping_cart)
  const theme = useTheme()
  const pvRoot: HTMLElement = document.querySelector('.surveyWidgetFullsizeContainer')
  const [showCart, setShowCart] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const viewport = useViewport()
  const [needHint, setNeedHint] = useState(true)
  const cardRefsMapRef = useRef<ImmutableMap<string, HTMLDivElement>>(ImmutableMap())
  const frameIdRef = useRef<number | null>(null)
  const currentCenterElementRef = useRef<string | null>(null)
  const isScrollingRef = useRef<boolean>(false)
  useLoadScriptInFrame('https://unpkg.com/@google/model-viewer', containerRef, viewport.mobile && viewport.emulated)

  const registerCardRef = useCallback((id: string, element: HTMLDivElement | null) => {
    if (element) {
      cardRefsMapRef.current = cardRefsMapRef.current.set(id, element)
    } else if (cardRefsMapRef.current.has(id)) {
      cardRefsMapRef.current = cardRefsMapRef.current.delete(id)
    }
  }, [])

  const findCenterElement = useCallback(() => {
    if (isScrollingRef.current) return
    isScrollingRef.current = true
    if (frameIdRef.current !== null) {
      cancelAnimationFrame(frameIdRef.current)
    }
    frameIdRef.current = requestAnimationFrame(() => {
      if (!containerRef.current || 0 === cardRefsMapRef.current.size) {
        isScrollingRef.current = false
        frameIdRef.current = null
        return
      }

      const containerRect = containerRef.current.getBoundingClientRect()
      const containerCenter = containerRect.top + containerRect.height / 2

      let closestElement = null
      let minDistance = Infinity

      cardRefsMapRef.current.forEach((element, id) => {
        const cardRect = element.getBoundingClientRect()
        const cardCenter = cardRect.top + cardRect.height / 2
        const distance = Math.abs(containerCenter - cardCenter)

        if (distance < minDistance) {
          minDistance = distance
          closestElement = id
        }
      })

      if (closestElement && closestElement !== currentCenterElementRef.current) {
        currentCenterElementRef.current = closestElement
      }

      isScrollingRef.current = false
      frameIdRef.current = null
    })
  }, [])

  const restorePosition = useCallback(() => {
    if (!currentCenterElementRef.current && containerRef.current) {
      containerRef.current.scrollTop = 0
    }
    if (cardRefsMapRef.current.has(currentCenterElementRef.current)) {
      const element = cardRefsMapRef.current.get(currentCenterElementRef.current)
      if (element) {
        requestAnimationFrame(() => {
          element.scrollIntoView({
            behavior: 'auto',
            block: 'center'
          })
        })
      }
    }
  }, [])

  const resetCenterElementRef = useCallback(() => {
    currentCenterElementRef.current = null
  }, [])

  useEffect(() => {
    if (!showCart && selectedIndex < 0) {
      restorePosition()
    }
  }, [showCart, selectedIndex, viewport.orientation, restorePosition])

  const scrollTop = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
    }
  }, [])

  const clearSelection = () => {
    if (showCart) {
      setShowCart(false)
      timeline.log({
        type: 'close_cart'
      })
    }
    if (selectedIndex >= 0) {
      timeline.log({
        type: 'close_popup',
        ref: response.answers[selectedIndex].id
      })
    }
    onUpdateResponse({
      answers: response.answers.map(r => {
        return { ...r, selected: false }
      }),
      clicked: response.clicked
    })
  }

  const onItemSelect = (index: number) => {
    scrollTop()
    if (showCart) {
      setShowCart(false)
      timeline.log({
        type: 'close_cart'
      })
    }
    response.answers[index].selected = true
    timeline.log({
      target: '0,0',
      type: 'open_popup',
      ref: response.answers[index].id
    })
    onUpdateResponse(response)
  }

  const onItemClose = () => {
    if (selectedIndex >= 0) {
      timeline.log({
        type: 'close_popup',
        ref: response.answers[selectedIndex].id
      })
      response.answers[selectedIndex].selected = false
      onUpdateResponse(response)
    }
  }

  const onCartOpen = () => {
    scrollTop()
    if (selectedIndex >= 0) {
      timeline.log({
        type: 'close_popup',
        ref: response.answers[selectedIndex].id
      })
      response.answers[selectedIndex].selected = false
      onUpdateResponse(response)
    }
    setShowCart(true)
    timeline.log({
      type: 'open_cart'
    })
  }

  const onCartClose = () => {
    setShowCart(false)
    timeline.log({
      type: 'close_cart'
    })
  }

  const onAddToCart = (qnt: number, index: number) => {
    response.answers[index].v += qnt
    if (selectedIndex < 0 && !showCart) {
      timeline.log({
        target: '0,0',
        type: 'open_popup',
        ref: response.answers[index].id
      })
    }
    timeline.log({
      type: showCart ? 'edit_in_cart' : 'add_to_cart',
      ref: response.answers[index].id,
      target: selectedIndex >= 0 || showCart ? response.answers[index].v.toString() : '1'
    })
    if ((!showCart && selectedIndex < 0) || (findability && selectedIndex >= 0)) {
      timeline.log({
        type: 'close_popup',
        ref: response.answers[index].id
      })
    }
    onUpdateResponse(response)
  }

  const onHomeClick = () => {
    showCart ? onCartClose() : onItemClose()
  }

  useEffect(() => {
    if (na) {
      const naResponse = response.answers.find(answer => answer.id === na.id)
      if (naResponse.v > 0) {
        checkout()
      }
    }
  }, [response, checkout, na])

  if (theme.style?.pv && !theme.style?.flutter && !pvRoot) return null

  return (
    <Dialog
      maskClassName={`${style.dialog} ${session.themeClassNames}`}
      appendTo={theme.style?.pv && pvRoot ? pvRoot : session.document?.body}
      onHide={() => {}}
      maximizable={true}
      maximized={true}
      visible={true}
    >
      <div
        className={`${style.container} ${viewport.mobile ? style.containerMobile : ''}`}
        onScroll={findCenterElement}
        style={{ overflowY: 'auto' }}
        ref={containerRef}
      >
        <Header
          schema={schema}
          total={totals[0]}
          findability={findability}
          clearSelection={clearSelection}
          onCartOpen={onCartOpen}
          onHomeClick={onHomeClick}
        />
        {selectedIndex >= 0 ? (
          <Product
            answer={schema.answers[selectedIndex]}
            schema={schema}
            response={response}
            index={selectedIndex}
            findability={findability}
            onItemClose={onItemClose}
            onCartOpen={onCartOpen}
            onItemSelect={onItemSelect}
            onAddToCart={onAddToCart}
            scrollTop={scrollTop}
          />
        ) : showCart ? (
          <Cart
            response={response}
            schema={schema}
            totals={totals}
            findability={findability}
            onCartClose={onCartClose}
            onAddToCart={onAddToCart}
            onItemSelect={onItemSelect}
            onUpdateResponse={onUpdateResponse}
            checkout={checkout}
            scrollTop={scrollTop}
          />
        ) : (
          <Main
            schema={schema}
            response={response}
            findability={findability}
            resetCenterElementRef={resetCenterElementRef}
            registerCardRef={registerCardRef}
            onItemSelect={onItemSelect}
            onAddToCart={onAddToCart}
            scrollTop={scrollTop}
          />
        )}
        <Footer
          findability={findability}
          showCart={showCart}
          showMain={!showCart && selectedIndex < 0}
          totals={totals[0]}
          onHomeClick={onHomeClick}
          onCartOpen={onCartOpen}
          showHint={() => setNeedHint(true)}
          closeSimulation={setClosed}
        />
      </div>
      <ExitSimulation
        setClosed={setClosed}
        needExternalHint={needHint}
        setExternalHint={viewport.mobile ? setNeedHint : null}
        hintText={schema.instruction}
      />
    </Dialog>
  )
}

export default CostcoView
